import { noop } from 'lodash-es';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { EM_DASH } from '~/common/constants/unicode';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { NumberInput } from '~/eds/src/components/NumberInput/NumberInput';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { Select } from '~/eds/src/components/Select/Select';
import { Text } from '~/eds/src/components/Text/Text';
import { copy } from '~/eds/src/utils/copy';
import { DocumentViewer } from '~/features/document-viewer/DocumentViewer';
import { HighlightSources } from '~/features/document/HighlightSources';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import { FieldValueDisplay } from '../fields/shared/FieldValue/FieldValueDisplay';
import { FieldValue } from '../fields/types';
import { TableValueDisplay } from '../ModelDetails/TableDetailsPanel/TableValueDisplay';
import { TableRowValue, TableValue } from '../types/table';
import { PromptModelWorkflow } from './types';
import { testCanRepublishWorkflowPrediction } from './utils';

interface Props {
  modelId: Uuid;
}

type DocIdType =
  | 'document_version'
  | 'document_handler'
  | 'document_external_id';

export const DebugModelByDocumentId = ({ modelId }: Props) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { navigate } = useRouting();
  const { toast } = useToast();
  const highlightMethod = useFlag(FlagType.CustomAiHighlightMethod);

  const documentViewer = useSelector(
    slices.xRay.selectors.selectDocumentViewer,
  );

  const workflowPrediction = useSelector(
    slices.xRayDebug.selectors.selectActiveWorkflowPrediction,
  );

  const [documentIdType, setDocumentIdType] =
    useState<DocIdType>('document_handler');

  const [documentId, setDocumentId] = useState<Nullable<number>>(null);

  const [getDocumentPrediction, { isFetching }] =
    api.endpoints.getPromptModelWorkflowPredictionByDocument.useLazyQuery();

  const [republishWorkflowPrediction, { isLoading: isRepublishing }] =
    api.endpoints.republishPromptModelWorkflowPrediction.useMutation();

  const workflowPredictionJson = JSON.stringify(workflowPrediction, null, 4);

  const displayType =
    documentIdType === 'document_version'
      ? intl.formatMessage({
          id: 'CustomAiDebugModel.documentVersionIdLabel',
          defaultMessage: 'a Document Version ID',
          description: 'Label for the document version identifier',
        })
      : documentIdType === 'document_external_id'
        ? intl.formatMessage({
            id: 'CustomAiDebugModel.externalDocumentIdLabel',
            defaultMessage: 'an External Document ID',
            description: 'Label for the external document identifier',
          })
        : intl.formatMessage({
            id: 'CustomAiDebugModel.documentHanlderIdLabel',
            defaultMessage: 'a Document Handler ID',
            description: 'Label for the document handler identifier',
          });

  const enableRepublish = workflowPrediction
    ? testCanRepublishWorkflowPrediction(workflowPrediction)
    : false;

  return (
    <Layout spacing={6}>
      <Layout
        direction="column"
        spacing={2}
        flex="1"
        maxW={workflowPrediction ? 'calc(100% - 504px)' : undefined}
      >
        <Layout align="center" justify="space-between" spacing={6}>
          <Layout spacing={2} align="flex-end" flex="auto">
            <Box flex="0">
              <FormField<DocIdType, false>
                input={Select}
                inputProps={{
                  isClearable: false,
                  options: [
                    {
                      label: intl.formatMessage({
                        id: 'CustomAiDebugModel.documentHanlderIdLabel2',
                        defaultMessage: 'Document Handler ID',
                        description:
                          'Label for the document handler identifier',
                      }),
                      value: 'document_handler',
                    },
                    {
                      label: intl.formatMessage({
                        id: 'CustomAiDebugModel.documentVersionIdLabel2',
                        defaultMessage: 'Document Version ID',
                        description:
                          'Label for the document version identifier',
                      }),
                      value: 'document_version',
                    },
                    {
                      disabled: true,
                      label: intl.formatMessage({
                        id: 'CustomAiDebugModel.externalDocumentIdLabel2',
                        defaultMessage: 'External Document ID',
                        description:
                          'Label for the external document identifier',
                      }),
                      value: 'document_external_id',
                    },
                  ],
                }}
                label={intl.formatMessage({
                  id: 'CustomAiDebugModel.selectDocumentIdType',
                  defaultMessage: 'Document ID Type',
                  description: 'Label for the document identifier type select',
                })}
                name="Document ID Type Select"
                value={documentIdType}
                onChange={(updatedDocIdType) => {
                  if (updatedDocIdType) {
                    setDocumentIdType(updatedDocIdType);
                  }
                }}
              />
            </Box>
            <Box flex="1">
              <FormField<Nullable<number>, false>
                input={NumberInput}
                label={intl.formatMessage({
                  id: 'CustomAiDebugModel.documentIdLabel',
                  defaultMessage: 'Document ID',
                  description: 'Label for the document identifier',
                })}
                name="Document ID Input"
                placeholder={intl.formatMessage(
                  {
                    id: 'CustomAiDebugModel.documentIdPlaceholderText',
                    defaultMessage: 'Enter {displayType}',
                    description:
                      'Placeholder text for the document identifier input. Example: displayType = "a Document Version ID"',
                  },
                  { displayType },
                )}
                value={documentId}
                onChange={setDocumentId}
              />
            </Box>
            <Button
              isLoading={isFetching}
              text={
                isFetching
                  ? intl.formatMessage({
                      id: 'CustomAiDebugModel.loadingDocumentPreviewButton',
                      defaultMessage: 'Loading Document…',
                      description:
                        'Button text when the document preview is loading',
                    })
                  : intl.formatMessage({
                      id: 'CustomAiDebugModel.loadDocumentPreviewButton',
                      defaultMessage: 'Load Document',
                      description:
                        'Button text to load the document preview for the document identifier entered',
                    })
              }
              onClick={
                documentId
                  ? () => {
                      getDocumentPrediction({
                        highlightMethod,
                        modelId,
                        documentId: String(documentId),
                        documentType: documentIdType,
                      })
                        .unwrap()
                        .then(
                          (updatedWorkflowPrediction?: PromptModelWorkflow) => {
                            const isTableModel =
                              updatedWorkflowPrediction?.output?.value_type ===
                              'table_value';
                            const highlights = isTableModel
                              ? ((
                                  updatedWorkflowPrediction?.output
                                    ?.value as TableRowValue[]
                                )?.flatMap((row: TableRowValue) =>
                                  row.cells.flatMap(
                                    (cell) => cell.highlights ?? [],
                                  ),
                                ) ?? [])
                              : ((
                                  updatedWorkflowPrediction?.output as FieldValue
                                )?.highlights ?? []);
                            switch (documentIdType) {
                              case 'document_handler':
                                dispatch(
                                  slices.xRay.actions.setDocumentViewer({
                                    documentId,
                                    highlights,
                                    activeHighlightId: highlights[0]?.id,
                                    invalidateActiveHighlightId: uuid(),
                                  }),
                                );
                                break;
                              case 'document_version':
                              default:
                                dispatch(
                                  slices.xRay.actions.setDocumentViewer({
                                    documentVersionId: documentId,
                                    highlights,
                                    activeHighlightId: highlights[0]?.id,
                                    invalidateActiveHighlightId: uuid(),
                                  }),
                                );
                                break;
                            }
                            dispatch(
                              slices.xRayDebug.actions.setActiveWorkflowPrediction(
                                updatedWorkflowPrediction ?? null,
                              ),
                            );
                          },
                        )
                        .catch(() => {
                          toast({
                            message: intl.formatMessage({
                              id: 'CustomAiDebugModel.loadDocumentFailed',
                              defaultMessage: 'Failed to load document.',
                              description:
                                'Notification message when the document fails to load',
                            }),
                            status: 'danger',
                          });
                        });
                    }
                  : undefined
              }
            />
          </Layout>
        </Layout>
        <Box border="border" h={560} flex="1 0 auto" pb={4}>
          <DocumentViewer {...documentViewer} />
        </Box>
      </Layout>
      {workflowPrediction && (
        <Panel
          footer={{
            actions: [
              {
                icon: 'clipboard',
                text: intl.formatMessage({
                  id: 'CustomAiDebugModel.copyPredictionJsonLabel',
                  defaultMessage: 'Copy Prediction JSON',
                  description:
                    'Label for the button to copy the prediction JSON to the clipboard',
                }),
                onClick: () => {
                  copy(workflowPredictionJson)
                    .then(() =>
                      toast({
                        message: intl.formatMessage({
                          id: 'CustomAiDebugModel.copiedPredictJsonSuccess',
                          defaultMessage: 'Copied',
                          description:
                            "Notification message when the JSON text was copied to the user's clipboard",
                        }),
                        status: 'success',
                      }),
                    )
                    .catch(noop);
                },
              },
              {
                disabled: !enableRepublish,
                isLoading: isRepublishing,
                level: 'danger',
                text: intl.formatMessage({
                  id: 'CustomAiDebugModel.republishButton',
                  defaultMessage: 'Republish Document',
                  description:
                    'Button text to republish the document workflow prediction',
                }),
                tooltip: enableRepublish
                  ? undefined
                  : intl.formatMessage({
                      id: 'CustomAiDebugModel.republishButtonDisabledTooltip',
                      defaultMessage:
                        'Only failed documents can be republished.',
                      description:
                        'Tooltip message explaining why the republish button is disabled',
                    }),
                onClick: () =>
                  republishWorkflowPrediction({
                    modelId,
                    workflowId: workflowPrediction.id,
                  })
                    .unwrap()
                    .then(() => {
                      toast({
                        status: 'success',
                        message: intl.formatMessage({
                          id: 'CustomAiDebugModel.republishSuccessMessage',
                          defaultMessage: 'Republishing the document…',
                          description:
                            'Notification message when republishing is triggered successfully.',
                        }),
                      });
                      // Refresh the page to get fresh publish progress metrics
                      navigate(
                        `${RoutePathType.AutomationHubFields}/${modelId}/debug`,
                      );
                    })
                    .catch(() => {
                      toast({
                        status: 'danger',
                        message: intl.formatMessage({
                          id: 'CustomAiDebugModel.republishFailedMessage',
                          defaultMessage: 'Failed to republish the document.',
                          description:
                            'Notification message when republishing fails.',
                        }),
                      });
                    }),
              },
            ],
          }}
          offsetTop={118}
          position="right"
          title={intl.formatMessage({
            id: 'CustomAiDebugModel.modelOutputTitle',
            defaultMessage: 'Model Output',
            description: 'Title for the model output details panel',
          })}
          width="panel.width.m"
        >
          <Layout direction="column" spacing={6}>
            <Layout direction="column">
              <Text variant="body-bold">
                <FormattedMessage
                  id="CustomAiDebugModel.sourcesLabel"
                  defaultMessage="Source(s)"
                  description="Label for the document source highlights"
                />
              </Text>
              <HighlightSources
                activeHighlightId={documentViewer.activeHighlightId}
                highlights={documentViewer.highlights ?? []}
                onHighlightSourceClick={(highlight) =>
                  dispatch(
                    slices.xRay.actions.setDocumentViewer({
                      ...documentViewer,
                      activeHighlightId: highlight.id,
                      invalidateActiveHighlightId: uuid(),
                    }),
                  )
                }
              />
            </Layout>
            <Layout direction="column">
              <Text variant="body-bold">
                <FormattedMessage
                  id="CustomAiDebugModel.valueLabel"
                  defaultMessage="Value"
                  description="Label for the model value"
                />
              </Text>
              {workflowPrediction.output ? (
                workflowPrediction.output.value_type === 'table_value' ? (
                  <TableValueDisplay
                    tableValue={workflowPrediction.output as TableValue}
                  />
                ) : (
                  <FieldValueDisplay
                    fieldValue={workflowPrediction.output as FieldValue}
                  />
                )
              ) : (
                <Text>{EM_DASH}</Text>
              )}
            </Layout>
          </Layout>
        </Panel>
      )}
    </Layout>
  );
};
